import { useContext, useEffect, useState, useCallback } from 'react';
import {
  keepSessionAlive,
  makePrivateClient,
  setOnTokenRefresh,
  type PrivateClientBundle,
  type TokenRefreshResponse,
} from '@/utils/apollo';
import { UserInfoContext } from '@/contexts/UserInfoContext';
import { ApolloProvider } from '@apollo/client';

// Tear down a superseded client bundle: stop the Apollo client AND disconnect
// its PhoenixSocket. `client.stop()` alone does NOT close the phoenix socket
// (@absinthe/socket owns it), so without `socket.disconnect()` the old socket
// keeps reconnecting forever — the memory leak (matrix-p2lc). This is disposal
// of an ALREADY-superseded socket; the new client's socket retries as normal.
const disposeBundle = (bundle: PrivateClientBundle) => {
  bundle.client.stop();
  bundle.socket?.disconnect();
};

// Refresh every 4 minutes — stays ahead of the ~5 min access token lifetime
// so the session is extended before the token expires.
const GLOBAL_KEEPALIVE_INTERVAL_MS = 4 * 60 * 1000;

/**
 * Exposes an instance of Apollo Client with authentication using a split link via sockets for
 * subscriptions and HTTP for everything else (queries and mutations).
 *
 * HTTP requests use session cookies for auth. WebSocket connections pass the JWT token
 * in params since cookies aren't reliably forwarded during WebSocket upgrades.
 *
 * The client is recreated when the JWT changes (e.g., after login or token refresh).
 *
 * Token Refresh Flow:
 * 1. When a 401 is received, apollo.ts calls /auth/refresh
 * 2. If successful, it notifies this provider via setOnTokenRefresh callback
 * 3. This provider updates the JWT and recreates the Apollo client with fresh token
 * 4. WebSocket connections are re-established with the new token
 */
export const PrivateApolloProvider = ({ children }: { children: React.ReactNode }) => {
  const { userInfo, setUserInfo } = useContext(UserInfoContext);
  const jwt = userInfo?.jwt || '';
  // `null` until the effect installs a socket-backed bundle. Never construct a
  // socket in the useState initializer — StrictMode double-invokes initializers
  // and would orphan the discarded socket (the very leak we're fixing). The
  // [jwt] effect owns all socket creation + disposal, and children render only
  // once the bundle exists (see the guard below) so no subscription binds to a
  // socketless client on the first tick.
  const [bundle, setBundle] = useState<PrivateClientBundle | null>(null);

  // Handle token refresh notifications from apollo.ts
  const handleTokenRefresh = useCallback(
    (response: TokenRefreshResponse) => {
      console.log('🔄 Token refresh notification received, updating JWT');

      // Update userInfo with new JWT
      if (userInfo && response.access_token !== userInfo.jwt) {
        setUserInfo({
          ...userInfo,
          jwt: response.access_token,
        });
      }
    },
    [userInfo, setUserInfo]
  );

  // Register the token refresh callback on mount
  useEffect(() => {
    setOnTokenRefresh(handleTokenRefresh);

    // Cleanup on unmount
    return () => {
      setOnTokenRefresh(null);
    };
  }, [handleTokenRefresh]);

  // Global session keepalive — extends the server-side session by calling
  // /auth/refresh periodically. This triggers a real Keycloak token exchange
  // which pushes session.expires_at forward. Without this, long-lived SPA pages
  // (like the workflow canvas) make no HTTP requests while the user edits, and
  // the session silently expires.
  useEffect(() => {
    if (!jwt) return; // Not authenticated

    const id = setInterval(() => {
      keepSessionAlive();
    }, GLOBAL_KEEPALIVE_INTERVAL_MS);

    return () => clearInterval(id);
  }, [jwt]);

  // Own the client bundle's lifecycle in a single [jwt] effect: create on setup,
  // dispose on cleanup. React pairs each setup with exactly one cleanup — incl.
  // StrictMode's dev setup→cleanup→setup — so a JWT change disposes the old
  // socket and connects a new one, the socket is recreated (not left dead) on a
  // StrictMode remount, and no socket is ever orphaned.
  useEffect(() => {
    console.log('🔄 (Re)creating Apollo client for current JWT');
    const next = makePrivateClient(jwt);
    setBundle(next);
    return () => disposeBundle(next);
  }, [jwt]);

  if (!bundle) return null;

  return <ApolloProvider client={bundle.client}>{children}</ApolloProvider>;
};
